// Top-level UI state helper for TeamsReportingPage
function getTeamsReportingUIState(
  rows: any[],
  teamsSearchMeta: any,
  forceNoResults: boolean,
  isPageLoading: boolean,
  hasSearchAttempted: boolean
) {
  const displayRows = rows;
  const totalItems = Number(teamsSearchMeta.total || rows.length);
  const itemsPerPage = Number(teamsSearchMeta.pageSize || TEAM_REPORTING_DEFAULT_PAGE_SIZE);
  const shouldShowLeadNoResultBanner = forceNoResults && displayRows.length > 0;
  const shouldRenderResults = !isPageLoading && (hasSearchAttempted || forceNoResults || rows.length > 0);
  const shouldShowReportDropdown = displayRows.length > 0;
  const shouldShowInlineReportDropdown = shouldShowLeadNoResultBanner && shouldShowReportDropdown;
  const shouldShowTopReportDropdown = shouldShowReportDropdown && !shouldShowInlineReportDropdown;
  return {
    displayRows,
    totalItems,
    itemsPerPage,
    shouldShowLeadNoResultBanner,
    shouldRenderResults,
    shouldShowInlineReportDropdown,
    shouldShowTopReportDropdown,
  };
}
import Dropdown, { DropdownOption } from "../../../components/common/Dropdown";

import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  selectTeamsReportingAreaOptions,
  selectTeamsReportingPageLoading,
  selectTeamsReportingRatingOptions,
  selectTeamsReportingRegionOptions,
  selectTeamsReportingRoleOptions,
  selectTeamsReportingLeadsDropdownOptions,
  selectTeamsReportingSearchMeta,
  selectTeamsReportingTableRows,
  selectIsPdfDownloadLoading,
  fetchTeamsReportingLeadsDropdown,
  fetchTeamsReportingTeamsSearch,
  fetchTeamsReportingRatingRoles,
  fetchAreaOptions,
  fetchRegionOptions,
  clearTeamsReportingRatingRolesState,
  resetDependentDropdowns,
  downloadTeamsReportingPDF,
  downloadTeamsReportingCSV,
  selectTeamsReportingCSVDownloadLoading,
  downloadP2PDetailReportingPDF,
  downloadP2PDetailReportingCSV,
  downloadMembershipCountCSV,
  selectMembershipCountLoading,
  selectMembershipCountError,
} from "../../../store/slices/teamsReportSlice";

import { useProfile } from "../../../contexts/ProfileContext";
import Loader from "../../../components/common/Loader";
import BreadCrumbs from "../../../components/common/BreadCrumbs/BreadCrumbs";
import { Link, useNavigate } from "react-router-dom";
import TeamsSearchContainer from "./TeamsSearchContainer";
import UBTSearchResults from "../../../components/common/UBTSearchResults";
import NoResultsFound from "../../../components/common/NoResultsFound";
import {
  TEAM_REPORTING_ALL_AREA_CODE,
  TEAM_REPORTING_COLUMNS,
  TEAM_REPORTING_DEFAULT_PAGE,
  TEAM_REPORTING_DEFAULT_PAGE_SIZE,
  TEAM_REPORTING_DEFAULT_SORT_COLUMN,
  TEAM_REPORTING_DEFAULT_SORT_DIRECTION,
  EXPORT_MEMBERSHIP_COUNT_CSV_ID,
} from "../../../constants/teamReportingConstant";
import { getTeamsReportingOptions } from "../../../constants/reportDropdownOptions";
import type {
  TeamReportingOption,
  TeamReportingSearchFilters,
  TeamReportingSortDirection,
  TeamReportingTableRow,
} from "../../../types/teamReporting.types";
import { loadSearchFromSession } from "../../../utils/searchSessionStorage";
import { teamStorage } from "../../../utils/teamStorage";

// Helper to dispatch PDF export
export function exportPDFReport(
  dispatch: ReturnType<typeof useAppDispatch>,
  lastSearchFilters: TeamReportingSearchFilters,
  sortColumn: string,
  sortDirection: string
) {
  dispatch(
    downloadTeamsReportingPDF({
      region: lastSearchFilters.region ? String(lastSearchFilters.region) : undefined,
      area: lastSearchFilters.area ? String(lastSearchFilters.area) : undefined,
      rating: lastSearchFilters.rating,
      roles: lastSearchFilters.roles,
      startDate: lastSearchFilters.startDate,
      endDate: lastSearchFilters.endDate,
      roleNuid: lastSearchFilters.roleNuid ? String(lastSearchFilters.roleNuid) : undefined,
      sortColumn: sortColumn,
      sortType: sortDirection as "ASC" | "DESC",
    })
  );
}

// Helper to dispatch CSV export
export function exportCSVReport(
  dispatch: ReturnType<typeof useAppDispatch>,
  lastSearchFilters: TeamReportingSearchFilters,
  sortColumn: string,
  sortDirection: string
) {
  dispatch(
    downloadTeamsReportingCSV({
      region: lastSearchFilters.region ? String(lastSearchFilters.region) : undefined,
      area: lastSearchFilters.area ? String(lastSearchFilters.area) : undefined,
      rating: lastSearchFilters.rating,
      roles: lastSearchFilters.roles,
      startDate: lastSearchFilters.startDate,
      endDate: lastSearchFilters.endDate,
      roleNuid: lastSearchFilters.roleNuid ? String(lastSearchFilters.roleNuid) : undefined,
      sortColumn: sortColumn,
      sortType: sortDirection as "ASC" | "DESC",
    })
  );
}

/**
 * Helper to dispatch the Export Membership Count CSV.
 * Passes current table sort column and direction so the exported file
 * matches the order the user currently sees in the results table.
 *
 * @param dispatch - Redux dispatch function.
 * @param sortBy - Current table sort column (e.g. "teamId").
 * @param sortOrder - Current table sort direction (e.g. "ASC").
 * @param nuid - Authenticated user NUID for the x-nuid header.
 */
export function exportMembershipCountCSV(
  dispatch: ReturnType<typeof useAppDispatch>,
  sortBy: string,
  sortOrder: string,
  nuid: string | null,
) {
  dispatch(
    downloadMembershipCountCSV({
      sortBy,
      sortOrder,
      nuid,
    }),
  );
}

/**
 * Custom hook to handle session restore and auto-search logic for TeamsReportingPage.
 */

function useTeamsReportingSessionRestore(
  regionOptions: TeamReportingOption[],
  areaOptions: TeamReportingOption[],
  fetchTeams: (
    filters: TeamReportingSearchFilters,
    nextPage?: number,
    nextSortColumn?: string,
    nextSortDirection?: TeamReportingSortDirection
  ) => void,
  setters: {
    setSelectedRegionCode: (code: string) => void,
    setSelectedAreaCode: (code: string) => void,
    setLastSearchFilters: (filters: TeamReportingSearchFilters) => void,
    setHasSearchAttempted: (v: boolean) => void,
    setShouldScrollToResults: (v: boolean) => void,
  }
) {
  const [sessionRestored, setSessionRestored] = useState(false);
  const [wasRestoredFromSession, setWasRestoredFromSession] = useState(false);
  const [storedSearch, setStoredSearch] = useState<any>(null);

  // Restore region/area from session
  useEffect(() => {
    if (sessionRestored || regionOptions.length === 0) return;
    const session = loadSearchFromSession();
    setStoredSearch(session);
    if (!session) {
      setSessionRestored(true);
      return;
    }
    if (session.selectedRegionCode) {
      setters.setSelectedRegionCode(String(session.selectedRegionCode.value || ""));
      setWasRestoredFromSession(true);
    }
    if (session.selectedAreaCode) {
      setters.setSelectedAreaCode(String(session.selectedAreaCode.value || ""));
    }
    setSessionRestored(true);
  }, [regionOptions.length, sessionRestored, setters]);

  // Auto-search after session restore
  useEffect(() => {
    if (!wasRestoredFromSession || !sessionRestored || regionOptions.length === 0) return;
    if (!areaOptions.length) return;
    let isMounted = true;
    const searchFilters: TeamReportingSearchFilters = {};
    if (storedSearch) {
      if (regionOptions.length) {
        const regionCode = regionOptions.find((opt) => String(opt.value) === String(storedSearch.selectedRegionCode?.value || ""));
        if (regionCode) searchFilters.region = regionCode.id;
      }
      if (areaOptions.length) {
        const areaCode = areaOptions.find((opt) => String(opt.value) === String(storedSearch.selectedAreaCode?.value || "") || String(opt.id) === String(storedSearch.selectedAreaCode?.value || ""));
        if (areaCode) searchFilters.area = areaCode.id;
      }
    }
    if (isMounted) {
      setters.setHasSearchAttempted(true);
      setters.setLastSearchFilters(searchFilters);
      setters.setShouldScrollToResults(true);
      fetchTeams(searchFilters, TEAM_REPORTING_DEFAULT_PAGE);
      setWasRestoredFromSession(false);
    }
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wasRestoredFromSession, sessionRestored, regionOptions, areaOptions, fetchTeams, storedSearch]);
}

/**
 * Builds the shared params object for both P2P detail report download thunks.
 */
export function buildP2PDownloadParams(
  lastSearchFilters: TeamReportingSearchFilters,
  sortColumn: string,
  sortDirection: string,
  nuid: string | null,
) {
  return {
    region: lastSearchFilters.region ? String(lastSearchFilters.region) : undefined,
    area: lastSearchFilters.area ? String(lastSearchFilters.area) : undefined,
    rating: lastSearchFilters.rating,
    roles: lastSearchFilters.roles,
    startDate: lastSearchFilters.startDate,
    endDate: lastSearchFilters.endDate,
    roleNuid: lastSearchFilters.roleNuid,
    sortColumn,
    sortType: sortDirection as "ASC" | "DESC",
    nuid,
  };
}

/**
 * Handles report download based on selected report type.
 * Passes search filter params from state to the API for filtered reports.
 * The membership count export also forwards the current table sort column
 * and direction so the downloaded CSV matches the on-screen order.
 */
export function handleReportExportHelper(
  selectedReport: DropdownOption,
  dispatch: ReturnType<typeof useAppDispatch>,
  lastSearchFilters: TeamReportingSearchFilters,
  sortColumn: string,
  sortDirection: string,
  nuid: string | null,
) {
  switch (selectedReport.id) {
    case "teams_reporting":
      exportPDFReport(dispatch, lastSearchFilters, sortColumn, sortDirection);
      break;
    case "export_team_summary_csv":
      exportCSVReport(dispatch, lastSearchFilters, sortColumn, sortDirection);
      break;
    case "p2P_detail_reporting_pdf":
      dispatch(downloadP2PDetailReportingPDF(buildP2PDownloadParams(lastSearchFilters, sortColumn, sortDirection, nuid)));
      break;
    case "p2P_detail_reporting_csv":
      dispatch(downloadP2PDetailReportingCSV(buildP2PDownloadParams(lastSearchFilters, sortColumn, sortDirection, nuid)));
      break;
    case EXPORT_MEMBERSHIP_COUNT_CSV_ID:
      exportMembershipCountCSV(dispatch, sortColumn, sortDirection, nuid);
      break;
    default:
      break;
  }
}

/**
 * Renders the active UBT team reporting page and coordinates filter-driven searches.
 * @returns The team reporting page.
 */
function TeamsReportingPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const { loading: profileLoading, profile } = useProfile();
  const regionOptions = useAppSelector(selectTeamsReportingRegionOptions);
  const areaOptions = useAppSelector(selectTeamsReportingAreaOptions);
  const ratingOptions = useAppSelector(selectTeamsReportingRatingOptions);
  const roleOptions = useAppSelector(selectTeamsReportingRoleOptions);
  const leadsDropdownOptions = useAppSelector(
    selectTeamsReportingLeadsDropdownOptions,
  );
  const rows = useAppSelector(selectTeamsReportingTableRows);
  const teamsSearchMeta = useAppSelector(selectTeamsReportingSearchMeta);
  const isPageLoading = useAppSelector(selectTeamsReportingPageLoading);
  const csvDownloadLoading = useAppSelector(selectTeamsReportingCSVDownloadLoading);
  const pdfDownloadLoading = useAppSelector(selectIsPdfDownloadLoading);
  const membershipCountLoading = useAppSelector(selectMembershipCountLoading);
  const membershipCountError = useAppSelector(selectMembershipCountError);
  const [showMembershipCountErrorBanner, setShowMembershipCountErrorBanner] = useState<boolean>(false);

  const [sortColumn, setSortColumn] = useState(
    TEAM_REPORTING_DEFAULT_SORT_COLUMN,
  );
  const [sortDirection, setSortDirection] =
    useState<TeamReportingSortDirection>(TEAM_REPORTING_DEFAULT_SORT_DIRECTION);
  const [page, setPage] = useState(TEAM_REPORTING_DEFAULT_PAGE);
  const [lastSearchFilters, setLastSearchFilters] =
    useState<TeamReportingSearchFilters>({});
  const [selectedRegionCode, setSelectedRegionCode] = useState<string>("");
  const [selectedAreaCode, setSelectedAreaCode] = useState<string>("");
  const [forceNoResults, setForceNoResults] = useState(false);
  const [hasSearchAttempted, setHasSearchAttempted] = useState(false);
  const [isSearchAllLeadRolesEnabled, setIsSearchAllLeadRolesEnabled] =
    useState(false);
  // session restore state now handled in custom hook
  const [shouldScrollToResults, setShouldScrollToResults] = useState(false);
  const navigate = useNavigate();
  const resultsTableRef = useRef<HTMLDivElement>(null);
  const noResultsBannerRef = useRef<HTMLDivElement>(null);
  // Use profile.primaryRegion as the source of regioncd
  const regioncd = profile?.primaryRegion ?? "";
  const nuid = profile?.nuid ? String(profile.nuid) : null;

  useEffect(() => {
    dispatch(fetchRegionOptions());
    dispatch(fetchTeamsReportingRatingRoles({ nuid }));
  }, [dispatch, nuid]);

  useEffect(() => {
    return () => {
      dispatch(clearTeamsReportingRatingRolesState());
    };
  }, [dispatch]);

  // Set the default region from profile when it becomes available,
  // but only if session storage doesn't have a region already
  useEffect(() => {
    if (profile?.primaryRegion && !selectedRegionCode) {
      const storedSearch = loadSearchFromSession();
      if (!storedSearch?.selectedRegionCode) {
        setSelectedRegionCode(String(profile.primaryRegion));
      }
    }
  }, [profile, selectedRegionCode]);

  useEffect(() => {
    if (!selectedRegionCode) return;
    dispatch(fetchAreaOptions({ regionCode: selectedRegionCode }));
  }, [dispatch, selectedRegionCode]);

  const fetchTeams = useCallback(
    (
      filters: TeamReportingSearchFilters,
      nextPage: number = page,
      nextSortColumn: string = sortColumn,
      nextSortDirection: TeamReportingSortDirection = sortDirection,
    ) => {
      const request = {
        ...filters,
        page: nextPage,
        per_page: TEAM_REPORTING_DEFAULT_PAGE_SIZE,
        sortColumn: nextSortColumn,
        sortType: nextSortDirection,
        nuid,
      };

      dispatch(fetchTeamsReportingTeamsSearch(request));
    },
    [dispatch, nuid, page, sortColumn, sortDirection],
  );


  // Use custom hook for session restore and auto-search
  useTeamsReportingSessionRestore(
    regionOptions,
    areaOptions,
    fetchTeams,
    {
      setSelectedRegionCode,
      setSelectedAreaCode,
      setLastSearchFilters,
      setHasSearchAttempted,
      setShouldScrollToResults,
    }
  );

  const handleRegionChange = useCallback(
    (regionOption: TeamReportingOption | null) => {
      const regionCode = regionOption?.id ? String(regionOption.id) : "";
      setSelectedRegionCode(regionCode);
      setSelectedAreaCode("");
      dispatch(resetDependentDropdowns({ level: "region" }));
    },
    [dispatch],
  );

  const handleAreaChange = useCallback(
    (areaOption: TeamReportingOption | null) => {
      setSelectedAreaCode(areaOption?.id ? String(areaOption.id) : "");
    },
    [],
  );

  useEffect(() => {
    // Only fetch leads dropdown when "Search all lead roles" checkbox is enabled
    if (!isSearchAllLeadRolesEnabled || !selectedRegionCode) {
      return;
    }

    dispatch(
      fetchTeamsReportingLeadsDropdown({
        regionCode: selectedRegionCode,
        areaCode: selectedAreaCode || TEAM_REPORTING_ALL_AREA_CODE,
        nuid,
      }),
    );
  }, [
    dispatch,
    nuid,
    selectedAreaCode,
    selectedRegionCode,
    isSearchAllLeadRolesEnabled,
  ]);

  const handleSort = useCallback(
    (col: string, dir: "ASC" | "DESC") => {
      setSortColumn(col);
      setSortDirection(dir);
      setPage(TEAM_REPORTING_DEFAULT_PAGE);
      fetchTeams(lastSearchFilters, TEAM_REPORTING_DEFAULT_PAGE, col, dir);
    },
    [fetchTeams, lastSearchFilters],
  );

  const handleSearch = useCallback(
    (searchFilters: TeamReportingSearchFilters) => {
      setHasSearchAttempted(true);
      setLastSearchFilters(searchFilters);
      setPage(TEAM_REPORTING_DEFAULT_PAGE);
      setShouldScrollToResults(true);
      
      fetchTeams(searchFilters, TEAM_REPORTING_DEFAULT_PAGE);
    },
    [fetchTeams],
  );

  const handleLeadMemberNoResultChange = useCallback(
    (showNoResult: boolean) => {
      if (showNoResult) {
        setHasSearchAttempted(true);
      }
      setForceNoResults(showNoResult);
    },
    [],
  );

  /**
   * Handles the "Search all lead roles" checkbox toggle.
   * When enabled, triggers the leads dropdown API to fetch available lead members.
   * @param checked - Whether the checkbox is checked.
   */
  const handleSearchAllLeadRolesChange = useCallback((checked: boolean) => {
    setIsSearchAllLeadRolesEnabled(checked);
  }, []);



  const handlePageChange = useCallback(
    (nextPage: number) => {
      setPage(nextPage);
      fetchTeams(lastSearchFilters, nextPage);
    },
    [fetchTeams, lastSearchFilters],
  );

  const handleBreadcrumbPrevious = (): void => {
    navigate("/home");
  };

  const handleActionClick = useCallback(
    (rowData: TeamReportingTableRow) => {
      const ubtId = rowData.ubtId ?? rowData.teamId;
      if (ubtId) {
        teamStorage.setTeamId(ubtId);
        navigate("/home/projects", {
          state: { teamId: ubtId, from: "Teams Reporting" },
        });
      }
    },
    [navigate],
  );

  const handleReportExport = useCallback(
    (selectedReport: DropdownOption) => {
      handleReportExportHelper(selectedReport, dispatch, lastSearchFilters, sortColumn, sortDirection, nuid);
    },
    [dispatch, lastSearchFilters, sortColumn, sortDirection, nuid],
  );



  const {
    displayRows,
    totalItems,
    itemsPerPage,
    shouldShowLeadNoResultBanner,
    shouldRenderResults,
    shouldShowInlineReportDropdown,
    shouldShowTopReportDropdown,
  } = getTeamsReportingUIState(
    rows,
    teamsSearchMeta,
    forceNoResults,
    isPageLoading,
    hasSearchAttempted
  );



interface ScrollToResultsParams {
  shouldScrollToResults: boolean;
  isPageLoading: boolean;
  hasSearchAttempted: boolean;
  forceNoResults: boolean;
  noResultsBannerRef: React.RefObject<HTMLDivElement>;
  resultsTableRef: React.RefObject<HTMLDivElement>;
  rows: any[];
  setShouldScrollToResults: (v: boolean) => void;
}

function scrollToResultsOrBanner(params: ScrollToResultsParams) {
  const {
    shouldScrollToResults,
    isPageLoading,
    hasSearchAttempted,
    forceNoResults,
    noResultsBannerRef,
    resultsTableRef,
    rows,
    setShouldScrollToResults,
  } = params;
  if (!shouldScrollToResults || isPageLoading) return;
  if (!hasSearchAttempted) return;
  requestAnimationFrame(() => {
    if (forceNoResults && noResultsBannerRef.current) {
      const bannerTop = noResultsBannerRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(0, bannerTop - 20), behavior: "smooth" });
    } else if (resultsTableRef.current && rows.length > 0) {
      const tableTop = resultsTableRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(0, tableTop - 20), behavior: "smooth" });
    }
    setShouldScrollToResults(false);
  });
}

  // Scroll to results table or no results banner after search completes
  useEffect(() => {
    scrollToResultsOrBanner({
      shouldScrollToResults,
      isPageLoading,
      hasSearchAttempted,
      forceNoResults,
      noResultsBannerRef,
      resultsTableRef,
      rows,
      setShouldScrollToResults,
    });
  }, [shouldScrollToResults, isPageLoading, hasSearchAttempted, forceNoResults, rows.length]);

  // Auto-dismiss membership count export error banner after 5 s
  useEffect(() => {
    if (!membershipCountError) return;
    setShowMembershipCountErrorBanner(true);
    const timer = setTimeout(() => {
      setShowMembershipCountErrorBanner(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [membershipCountError]);

  if (profileLoading) return <Loader />;
  
  return (
    <div className="min-h-[70vh] lg:mx-40! mx-5!">
      <div className="flex justify-between items-end ">
        <div>
          <BreadCrumbs
            previousPage="UBT Home"
            onPreviousClick={handleBreadcrumbPrevious}
          />
        </div>

        <div className="mobile-margin lg:mt-15! ">
          <Link
            to="/home/my-teams"
            data-analytics-type="link"
            className="no-underline! p1 -medium"
          >
            My Teams
          </Link>
          <Link
            to="/home/select-ubt"
            data-analytics-type="link"
            className="no-underline! p1 -medium pl-6! "
          >
            Find and Select a UBT
          </Link>
          <Link
            to="/home/search-teams"
            data-analytics-type="link"
            className="no-underline! p1 -medium pl-6!"
          >
            Search UBTs Projects
          </Link>
        </div>
      </div>

      <div className="flex justify-between items-end mb-4! md:mb-0! desktop:pb-4! margin-top-4u desktop:mt-12! md:mt-0! mobile:mt-3.5!">
        <div className="">
          <h1 className="h1 tablet:mt-8! mobile:mt-3.5!">
            UBT teams reporting
          </h1>
          <p className="p3 -book mt-2 md:mb-0!">
            Click <span className="-medium">search</span> to search for
            employees by their names and region.
          </p>
        </div>
      </div>

      <TeamsSearchContainer
        regioncd={regioncd}
        regionOptions={regionOptions}
        areaOptions={areaOptions}
        leadsDropdownOptions={leadsDropdownOptions}
        ratingOptions={ratingOptions}
        rolesOptions={roleOptions}
        onRegionChange={handleRegionChange}
        onAreaChange={handleAreaChange}
        onSearchAllLeadRolesChange={handleSearchAllLeadRolesChange}
        onLeadMemberNoResultChange={handleLeadMemberNoResultChange}
        onSearch={handleSearch}
        restoredRegionCode={selectedRegionCode}
        restoredAreaCode={selectedAreaCode}
      />
      {shouldShowTopReportDropdown ? (
        <div className="flex justify-end my-4! items-end!">
          <div className="flex gap-4! items-center!">
            {(pdfDownloadLoading || csvDownloadLoading || membershipCountLoading) && <Loader />}
            <Dropdown
              options={getTeamsReportingOptions()}
              menuClassName="w-full! min-w-85!"
              buttonClassName="w-full! min-w-50!"
              onSelect={handleReportExport}
              buttonLabel="Select a Report"
              disabled={pdfDownloadLoading || csvDownloadLoading || membershipCountLoading}
            />
          </div>
        </div>
      ) : null}

      {showMembershipCountErrorBanner && membershipCountError ? (
        <div role="alert" className="mt-2! mb-2! text-red-600 text-sm p-2 border border-red-300 rounded">
          {membershipCountError}
        </div>
      ) : null}

      {shouldRenderResults ? (
        <div className="mt-8!" ref={resultsTableRef}>
          {shouldShowLeadNoResultBanner ? (
            <div className="mb-8! flex justify-between items-start gap-6!" ref={noResultsBannerRef}>
              <NoResultsFound
                dsCardClassName="w-1/2!"
                title="No results match the current search criteria"
                message="There are no matches found for your search criteria."
              />
              {shouldShowInlineReportDropdown ? (
                <div className="flex pt-2! mt-10! gap-4! items-center!">
                  {(pdfDownloadLoading || csvDownloadLoading || membershipCountLoading) && <Loader />}
                  <Dropdown
                    options={getTeamsReportingOptions()}
                    menuClassName="w-full! min-w-85!"
                    buttonClassName="w-full! min-w-50!"
                    onSelect={handleReportExport}
                    buttonLabel="Select a Report"
                    disabled={pdfDownloadLoading || csvDownloadLoading || membershipCountLoading}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          <UBTSearchResults
            rows={displayRows}
            columns={TEAM_REPORTING_COLUMNS}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            currentPage={page}
            onPageChange={handlePageChange}
            onSort={handleSort}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            hidePaginationCount={false}
            actionKey="ubtName"
            onActionClick={handleActionClick}
          />
        </div>
      ) : null}
      {isPageLoading ? <Loader /> : null}
    </div>
  );
}

export default TeamsReportingPage;
